import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { getV1MessagingChannelsMessagingChannelIdMessages } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { MessagingMessage } from '~hooks/useMessaging/models';
import useReportRequestError from '~hooks/useReportRequestError';
import { messagingTranscriptQueryKey } from '~queries/messagingQueryKeys';
import { extractPagination } from '~services/pagination';
import { Nullable } from '~types/Nullable';

const MESSAGES_PAGE_SIZE = 50;

export type MessagingMessagesPage = {
  messages: MessagingMessage[];
  nextCursor: string | undefined;
};

/** The api orders newest first, so the accumulated pages are reversed for display. */
export default function useMessagingMessagesInfiniteQuery({
  channelId,
  companyId,
}: {
  channelId: Nullable<string>;
  companyId: Nullable<string>;
}) {
  const query = useInfiniteQuery({
    queryKey: messagingTranscriptQueryKey(channelId),
    initialPageParam: undefined as string | undefined,
    queryFn:
      companyId && channelId
        ? async ({ pageParam, signal }): Promise<MessagingMessagesPage> => {
            const response = await getV1MessagingChannelsMessagingChannelIdMessages({
              path: { 'messaging-channel-id': channelId },
              query: {
                'page[limit]': MESSAGES_PAGE_SIZE,
                ...(pageParam ? { 'page[after]': pageParam } : {}),
              },
              signal,
            });

            return {
              messages: response.data.data.map(MessagingMessage.parse),
              nextCursor: extractPagination(response).after,
            };
          }
        : skipToken,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // The transcript offers its own Retry, so a backoff would only hold the spinner up.
    retry: false,
  });

  // Only a failed first or older page has an inline Retry; a failed refetch keeps stale messages on screen.
  useReportRequestError({
    error: query.error,
    errorUpdatedAt: query.errorUpdatedAt,
    message: t('error_messages.messaging.failed_to_fetch_messages'),
    shouldReport: query.isRefetchError,
  });

  const messages = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((page) => page.messages)
        .slice()
        .reverse(),
    [query.data?.pages],
  );

  return {
    fetchOlder: query.fetchNextPage,
    hasLoadFailed: query.isLoadingError,
    hasOlder: query.hasNextPage,
    hasOlderLoadFailed: query.isFetchNextPageError,
    isLoading: query.isLoading,
    isLoadingOlder: query.isFetchingNextPage,
    isRefreshing: query.isRefetching,
    messages,
    retryLoad: query.refetch,
  };
}
